import { SiteMeta } from '@src/types/SiteMeta';
import { Interval, IntervalUnit, SavedSearch } from '@types';
import { ReactElement, useEffect, useMemo } from 'react';
import { currentTzString } from '@utils';
import Input from '@components/_elements/Input/Input';
import {
  AGREG_TYPES,
  checkShouldAggr,
  intervalDict,
  POINTS_LIMIT,
} from './historianUtils';
import DateTimePicker from './DateTimePicker';

interface InputsProps {
  interval: Interval;
  setSearchField: <T>(field: string, value: T) => void;
  activeSearch: SavedSearch;
}
interface InputProps {
  value: number;
  type: string;
  placeholder: string;
  className: string;
  onChange: (val: string) => void;
  options?: IntervalUnit[];
}

const s = {
  gridTimeStamp: {
    alignItems: 'center',
  },
  label: {
    lineHeight: '1',
    paddingTop: 20,
  },
} as const;
function Inputs(props: InputsProps): ReactElement {
  const { interval, setSearchField, activeSearch } = props;

  const filteredOptions = useMemo(() => {
    const allOptions = [
      'millisecond',
      'second',
      'minute',
      'hour',
      'day',
      'month',
    ] as IntervalUnit[];

    if (!activeSearch.startDateTime || !activeSearch.stopDateTime) {
      return allOptions;
    }

    const startTime = activeSearch.startDateTime.valueOf();
    const endTime = activeSearch.stopDateTime.valueOf();
    const timeRangeMs = endTime - startTime;

    if (timeRangeMs <= 0 || !interval.value) {
      return allOptions;
    }

    return allOptions.filter((unit) => {
      const intervalMs = interval.value * intervalDict[unit] * 1000;
      const pointsCount = Math.ceil(timeRangeMs / intervalMs);
      return pointsCount <= POINTS_LIMIT;
    });
  }, [activeSearch.startDateTime, activeSearch.stopDateTime, interval.value]);

  useEffect(() => {
    if (
      !activeSearch.startDateTime ||
      !activeSearch.stopDateTime ||
      !interval.unit ||
      !interval.value
    ) {
      return;
    }

    const startTime = activeSearch.startDateTime.valueOf();
    const endTime = activeSearch.stopDateTime.valueOf();
    const timeRangeMs = endTime - startTime;

    if (timeRangeMs <= 0) {
      return;
    }

    const currentIntervalMs =
      interval.value * intervalDict[interval.unit] * 1000;
    const currentPointsCount = Math.ceil(timeRangeMs / currentIntervalMs);

    if (currentPointsCount > POINTS_LIMIT && filteredOptions.length > 0) {
      const allOptions = [
        'millisecond',
        'second',
        'minute',
        'hour',
        'day',
        'month',
      ] as IntervalUnit[];

      for (let i = 0; i < allOptions.length; i++) {
        const unit = allOptions[i];
        const intervalMs = interval.value * intervalDict[unit] * 1000;
        const pointsCount = Math.ceil(timeRangeMs / intervalMs);

        if (pointsCount <= POINTS_LIMIT) {
          setSearchField<Interval>('interval', {
            ...interval,
            unit,
          });
          break;
        }
      }
    }
  }, [
    activeSearch.startDateTime,
    activeSearch.stopDateTime,
    interval,
    filteredOptions,
    setSearchField,
  ]);

  const data = [
    {
      value: interval.value,
      type: 'number',
      placeholder: '',
      className: 'dark xs mr',
      onChange: (val: string): void => {
        setSearchField<Interval>('interval', {
          ...interval,
          value: Number(val),
        });
      },
      options: undefined,
    },
    {
      type: 'dropdown',
      placeholder: 'Interval',
      value: interval.unit ? { value: interval.unit } : false,
      className: 'width-s',
      onChange: (val: number): void => {
        setSearchField<Interval>('interval', {
          ...interval,
          unit: filteredOptions[val],
        });
      },
      options: filteredOptions,
    },
  ] as InputProps[];

  return (
    <div className='interval-row'>
      {data.map(
        (inputProps: InputProps): ReactElement => (
          <Input
            key={inputProps.type}
            value={inputProps.value}
            type={inputProps.type}
            placeholder={inputProps.placeholder}
            className={inputProps.className}
            onChange={inputProps.onChange}
            options={inputProps.options as never[]}
          />
        ),
      )}
    </div>
  );
}
interface AggregationInputProps {
  setSearchField: <T>(field: string, value: T) => void;
  activeSearch: SavedSearch;
}
function AggregationInput(props: AggregationInputProps): ReactElement {
  const { activeSearch, setSearchField } = props;
  return (
    <Input
      type='multiselect-dropdown'
      placeholder='Interval'
      value={activeSearch.aggregationFunctions}
      className='width-s'
      styleDD={{ width: '163px' }}
      onChange={(val: string[]): void =>
        setSearchField('aggregationFunctions', val)
      }
      options={AGREG_TYPES as never[]}
    />
  );
}
interface SettingsRowProps {
  siteMeta: SiteMeta;
  timeMode: string;
  activeSearch: SavedSearch;
  setSearchField: <T>(field: string, value: T) => void;
}
function SettingsRow(props: SettingsRowProps): ReactElement {
  const { siteMeta, timeMode, activeSearch, setSearchField } = props;
  const currentTz = currentTzString({
    timeMode,
    lat: siteMeta.GPSLat,
    long: siteMeta.GPSLong,
  });
  const aggrMessage =
    !checkShouldAggr(activeSearch) && 'Displayed aggregation types';
  return (
    <>
      <div className='label' style={s.label}>
        Timestamp
        <br />
        <br />
        <span>{currentTz}</span>
      </div>
      <div className='inputs-row no-pd-top with-buttons-search'>
        <div style={s.gridTimeStamp} className='grid-timestamp'>
          <div className='small-lable'>From</div>
          <div className='small-lable' />
          <div className='small-lable'>To</div>
          <div className='small-lable pd-l'>Interval</div>
          <div className='small-lable pd-l'>{aggrMessage}</div>
          <DateTimePicker
            field='startDateTime'
            activeSearch={activeSearch}
            setSearchField={setSearchField}
          />
          <div>&nbsp;-&nbsp;</div>
          <DateTimePicker
            field='stopDateTime'
            activeSearch={activeSearch}
            setSearchField={setSearchField}
          />
          <div className='pd-l dis'>
            <Inputs
              interval={activeSearch.interval}
              setSearchField={setSearchField}
              activeSearch={activeSearch}
            />
          </div>
          <div className='pd-l dis'>
            {!checkShouldAggr(activeSearch) && (
              <AggregationInput
                setSearchField={setSearchField}
                activeSearch={activeSearch}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SettingsRow;
